import React, { useState, useEffect } from 'react'
import classNames from 'classnames'
import Slider from '@toasttab/shared/src/components/organisms/MDS/Slider/Slider'
import {
  Asset,
  Image,
  Video
} from '@toasttab/shared/src/interfaces/ContentfulInterface.d'
import { ContentfulImageAPI } from '@toasttab/shared/src/utils/contentfulUtils'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { microcopyTransform } from '@toasttab/shared/src/utils/contentfulUtils'
import { BadgeMDSProps } from '@toasttab/shared/src/components/atoms/Badge/BadgeMDS.d'

interface ContentfulMdsShowcaseTestimonialsProps {
  heading?: string
  eyebrow?: string
  description?: any
  testimonials?: ContentfulMdsTestimonialProps[]
  hideSlider?: boolean
  sliderContainerClasses?: string
  imageContainerClasses?: string
  videoButtonClasses?: string
  fullWidth?: boolean
  alignInlineQuoteLeft?: boolean
  cardToggleSwitch2?: boolean
  noPadding?: boolean
  uniqueId?: string
  testId?: string
  __typename?: string
  contentful_id?: string
  dataTestId?: string
}

type asset = Image | Video

interface ContentfulMdsTestimonialProps {
  asset?: asset
  author?: string
  badge?: BadgeMDSProps
  description?: any
  eyebrow?: string
  heading?: any
  icon?: Asset
  jobTitle?: string
  links?: []
  location?: string
  title?: string
}

const ContentfulMdsShowcaseTestimonials = ({
  heading,
  eyebrow,
  description,
  testimonials,
  hideSlider,
  sliderContainerClasses,
  imageContainerClasses,
  videoButtonClasses,
  fullWidth,
  alignInlineQuoteLeft,
  cardToggleSwitch2 = true,
  noPadding,
  uniqueId,
  dataTestId = 'ContentfulMdsShowcaseTestimonials-1'
}: ContentfulMdsShowcaseTestimonialsProps) => {
  const [Slides, setSlides] = useState([])
  const { microCopyData } = usePageContext()

  const imageContainerCssClasses = classNames(imageContainerClasses, {
    'max-h-[341px]': hideSlider
  })

  // This showcase-testimonials class is only used for child targeting within the SectionWrapper component
  sliderContainerClasses = classNames(
    sliderContainerClasses,
    'showcase-testimonials'
  )

  if (noPadding) {
    sliderContainerClasses = classNames(sliderContainerClasses, 'mt-0')
  }

  useEffect(() => {
    const testimonialsSlides = testimonials?.map(
      (testimonial: ContentfulMdsTestimonialProps, index: number) => {
        let icon: any = []
        if (testimonial.icon) {
          icon = [
            {
              extension: testimonial.icon.file?.contentType,
              title: testimonial.icon.title,
              url: testimonial.icon.file?.url,
              lazyload: true,
              optimizedImages: {
                src: '',
                srcset: '',
                srcsetAvif: '',
                srcAvif: ''
              }
            }
          ]
        }
        let images: any = []
        let vidyardId: string = null
        const query = ContentfulImageAPI.transformImageAvif({
          width: 552,
          height: 404
        })
        let srcsetWeb = ''
        if (testimonial.asset) {
          switch (testimonial.asset.__typename) {
            case 'ContentfulMdsImage':
              const asset = testimonial.asset as Image
              srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
                url: asset.desktop?.file?.url,
                extension: asset.desktop?.file?.contentType
              })

              images = [
                {
                  extension: asset.desktop?.file?.contentType,
                  title: asset.desktop?.title,
                  url: asset.desktop?.file?.url,
                  lazyload: true,
                  optimizedImages: {
                    src: asset.desktop?.file?.url,
                    srcset: '',
                    srcsetAvif: srcsetWeb,
                    srcAvif: asset.desktop?.file?.url + query
                  }
                }
              ]
              break
            case 'ContentfulMdsVideo':
              const video = testimonial.asset as Video
              vidyardId = video.vidyardId
              srcsetWeb = ContentfulImageAPI.transformImageAvifSrcset({
                url: video.previewAsset?.file?.url,
                extension: video.previewAsset?.file?.contentType
              })

              images = [
                {
                  extension: video.previewAsset?.file?.contentType,
                  title: video.previewAsset?.title,
                  url: video.previewAsset?.file?.url,
                  lazyload: true,
                  classes: imageContainerCssClasses,
                  optimizedImages: {
                    src: video.previewAsset?.file?.url,
                    srcset: '',
                    srcsetAvif: srcsetWeb,
                    srcAvif: video.previewAsset?.file?.url + query
                  }
                }
              ]
              break
            default:
              break
          }
        }

        return {
          alignInlineQuoteLeft: alignInlineQuoteLeft,
          badge: testimonial.badge,
          bio: testimonial.jobTitle,
          cardClasses: noPadding ? 'mt-[-32px]' : '',
          ctaImage: [] as any,
          ctaLabel: testimonial.author,
          dataTestId: `TestimonialCard-${index + 1}`,
          eyebrow: eyebrow,
          heading: null as any,
          icon: icon,
          image: images,
          links: testimonial.links?.map((link: any) => {
            return {
              ctaLink: link.url,
              ctaLabel: link.label,
              linkIcons: link.icon
            }
          }),
          restaurantName: testimonial.location,
          sectionDescription: testimonial.description?.description,
          sectionHeading: testimonial.heading,
          toggleSwitch: false,
          toggleSwitch2: cardToggleSwitch2,
          typeHandle: 'testimonialCard',
          videoButtonClasses: videoButtonClasses,
          vidyardId: vidyardId
        }
      }
    )
    setSlides(testimonialsSlides)
  }, [])

  if (Slides?.length === 0) return null

  let sliderCss
  if (Slides.some((slide) => !slide.images?.length)) {
    sliderCss = '!mt-0 md:!mt-6'
  }
  return (
    <Slider
      bgColor='bg-white'
      centerActiveSlide={true}
      cssClasses={sliderContainerClasses}
      dataTestId={dataTestId}
      fullWidth={fullWidth}
      hideSlider={hideSlider}
      initSlider={Slides?.length > 1 ? true : false}
      pagination
      previousNextButtons={Slides?.length > 1 ? 'slide-nav-blue-75' : 'null'}
      sectionHeading={heading}
      sliderCss={sliderCss}
      sliderLoop={false}
      slides={Slides}
      slidesToShow={1}
      textAlignment='center'
      textAlignmentMobile='center'
      toggleSwitch={false}
      toggleSwitch2={true}
      uniqueId={uniqueId}
      sectionDescription={
        description?.description
          ? microcopyTransform(microCopyData, description?.description)
          : null
      }
    />
  )
}

export default ContentfulMdsShowcaseTestimonials
